import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { PropertySummary } from '../lib/api/properties';

export function useFeaturedProperties() {
  const [featuredProperties, setFeaturedProperties] = useState<PropertySummary[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchProperties = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      console.log('🚀 useFeaturedProperties: Fetching directly from Supabase...');
      
      const { data, error: supabaseError } = await supabase
        .from('properties')
        .select('*')
        .eq('status', 'active')
        .eq('featured', true)
        .order('created_at', { ascending: false })
        .limit(6);

      if (supabaseError) {
        throw supabaseError;
      }

      console.log(`✅ useFeaturedProperties: Fetched ${data?.length || 0} properties`);
      
      // Transform raw data to PropertySummary format
      const transformedData: PropertySummary[] = (data || []).map((p: any) => ({
        id: p.id,
        title: p.title,
        property_type: p.property_type,
        status: p.status,
        price: p.base_price || 0,
        bedroom_count: p.bedroom_count,
        bathroom_count: p.bathroom_count,
        address: p.address,
        location: p.location,
        owner_id: p.owner_id,
        created_at: p.created_at,
        updated_at: p.updated_at,
        images: p.photos || [], // Map photos to images
        amenities: [] // Default empty if not fetching relations
      }));

      setFeaturedProperties(transformedData);
    } catch (err: any) {
      console.error('❌ useFeaturedProperties: Error fetching properties:', err);
      setError(err.message || 'Failed to fetch featured properties');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProperties();
  }, [fetchProperties]);

  return {
    featuredProperties,
    isLoading,
    error,
    refreshFeaturedProperties: fetchProperties
  };
}
